import Link from "next/link";
import React from "react";
import { Form as BSForm, Stack } from "react-bootstrap";
import { Field, Form } from "react-final-form";
import { FormSubmitButton } from "src/components/form/FormSubmitButton";
import { TextField } from "src/components/form/TextField";
import { useTranslations } from "src/state/TranslationsContext";
import { AppRoutes } from "src/util/appRoutes";

import { Alert } from "@ream/ui";
import { validateSignupData } from "@ream/utils";
import { useState } from "react";
import { useAuth } from "src/state/AuthContext";
import { extractErrorMessage } from "src/util/api/apiError";
import { composeValidators, requireEmail, required } from "src/util/validators";

export type SignupFormValues = {
  name: string;
  email: string;
  companyName: string;
  fax?: string;
};

const OrgSignupFormInputs = () => {
  const { t } = useTranslations();

  return (
    <Stack gap={3}>
      <Field
        autoFocus
        name="name"
        label={t("client.signup.signup_form.name")}
        placeholder={t("client.signup.signup_form.name_placeholder")}
        suppressErrorUntilSubmitted
        component={TextField}
        validate={composeValidators(
          required(t("client.signup.signup_form.name_required")),
        )}
        data-test="signup-form-name"
      />

      <Field
        type="email"
        name="email"
        label={t("client.signup.signup_form.email")}
        placeholder={t("client.signup.signup_form.email_placeholder")}
        suppressErrorUntilSubmitted
        component={TextField}
        validate={composeValidators(
          required(t("client.signup.signup_form.email_required")),
          requireEmail(t("client.signup.signup_form.email_required")),
        )}
        data-test="signup-form-email"
      />

      <Field
        name="companyName"
        label={t("client.signup.signup_form.company_name")}
        placeholder={t("client.signup.signup_form.company_name_placeholder")}
        suppressErrorUntilSubmitted
        component={TextField}
        validate={composeValidators(
          required(t("client.signup.signup_form.company_name_required")),
        )}
        data-test="signup-form-company_name"
      />

      <Field
        name="fax"
        label={t("client.signup.signup_form.fax")}
        placeholder={t("client.signup.signup_form.fax_placeholder")}
        className="d-none"
        component={TextField}
      />

      <FormSubmitButton data-test="signup-form-submit">
        {t("client.signup.signup_form.submit")}
      </FormSubmitButton>

      <div className="small">
        <BSForm.Text>{t("client.signup.disclaimer")}</BSForm.Text>
      </div>

      <Link href={AppRoutes.login()} className="small">
        {t("client.signup.signup_form.login")}
      </Link>
    </Stack>
  );
};

export const OrgSignupForm: React.FC = () => {
  const { t } = useTranslations();

  const { register } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (formData: SignupFormValues) => {
    const { email, name, companyName, fax } = formData;
    setError(null);

    try {
      const errorMessage = validateSignupData(name, email, fax);

      if (errorMessage) {
        setError(errorMessage);
        return;
      }

      await register(name, email, companyName);
      setSuccess(true);
    } catch (error: any) {
      setError(
        extractErrorMessage(error, t("client.signup.error_during_login")),
      );
    }
  };

  if (success) {
    return (
      <Alert variant="success" className="mb-3">
        {t("client.signup.success")}
      </Alert>
    );
  }

  return (
    <>
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Form onSubmit={onSubmit}>
        {({ handleSubmit }) => {
          return (
            <BSForm onSubmit={handleSubmit}>
              <OrgSignupFormInputs />
            </BSForm>
          );
        }}
      </Form>
    </>
  );
};
